import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  Play,
  Download,
  Image as ImageIcon,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  ArrowLeft,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type Image = {
  id: string;
  originalName: string;
  size: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: number;
};

type Annotation = {
  id: string;
  imageId: string;
  imageName: string;
  result: {
    product_name: { value: string | null; confidence: number };
    brand: { value: string | null; confidence: number };
    specification: { value: string | null; confidence: number };
    price: { value: string | null; confidence: number };
    shelf_life: { value: string | null; confidence: number };
    production_date: { value: string | null; confidence: number };
    ingredients: { value: string | null; confidence: number };
    origin: { value: string | null; confidence: number };
  };
  confidence: number;
  reviewed: boolean;
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, updateQuota } = useAuthStore();
  const [project, setProject] = useState<any>(null);
  const [images, setImages] = useState<Image[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [annotating, setAnnotating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ image: Image; annotation?: Annotation } | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;

    try {
      const [projectResult, imagesResult, annotationsResult] = await Promise.all([
        api.projects.get(id),
        api.images.list(id),
        api.annotations.getByProject(id),
      ]);

      setProject(projectResult.data);
      setImages(imagesResult.data || []);
      setAnnotations(annotationsResult.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0 || !id) return;

    setUploading(true);
    try {
      await api.images.upload(id, Array.from(files));
      await loadData();
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上传失败');
    } finally {
      setUploading(false);
    }
  }

  async function handleStartAnnotation() {
    if (!id) return;

    const pendingImages = images.filter((img) => img.status === 'pending');
    if (pendingImages.length === 0) {
      alert('没有待标注的图片');
      return;
    }

    if ((user?.quota || 0) < pendingImages.length) {
      alert(`配额不足，需要 ${pendingImages.length}，剩余 ${user?.quota || 0}`);
      return;
    }

    if (!confirm(`确定要开始标注 ${pendingImages.length} 张图片吗？`)) return;

    setAnnotating(true);
    try {
      const result = await api.annotations.start(id);
      updateQuota(-result.data.success);
      await loadData();
      alert(`标注完成！成功 ${result.data.success} 张，失败 ${result.data.failed} 张`);
    } catch (error: any) {
      console.error('Annotation failed:', error);
      alert(error.message || '标注失败');
    } finally {
      setAnnotating(false);
    }
  }

  async function handleDeleteImage(imageId: string) {
    if (!confirm('确定要删除这张图片吗？')) return;

    try {
      await api.images.delete(imageId);
      await loadData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }

  function viewImageDetail(image: Image) {
    const annotation = annotations.find((a) => a.imageId === image.id);
    setSelectedImage({ image, annotation });
  }

  function getStatusBadge(status: string) {
    const config = {
      pending: { label: '待标注', color: 'bg-gray-100 text-gray-700', icon: Clock },
      processing: { label: '处理中', color: 'bg-blue-100 text-blue-700', icon: Clock },
      completed: { label: '已完成', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      failed: { label: '失败', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    };

    const { label, color, icon: Icon } = config[status as keyof typeof config] || config.pending;

    return (
      <Badge className={`${color} flex items-center space-x-1`}>
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </Badge>
    );
  }

  function exportData(format: 'json' | 'csv') {
    if (!id) return;
    const url = format === 'json' ? api.export.json(id) : api.export.csv(id);
    window.open(url, '_blank');
  }

  if (loading) {
    return <div className="text-center py-12">加载中...</div>;
  }

  if (!project) {
    return <div className="text-center py-12">项目不存在</div>;
  }

  const pendingCount = images.filter((img) => img.status === 'pending').length;
  const completedCount = images.filter((img) => img.status === 'completed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/projects">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold">{project.name}</h1>
            <p className="text-gray-500 mt-1">
              创建于 {new Date(project.createdAt).toLocaleDateString('zh-CN')}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => exportData('csv')}
            disabled={completedCount === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            导出 CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => exportData('json')}
            disabled={completedCount === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            导出 JSON
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">总图片</p>
                <p className="text-2xl font-bold">{images.length}</p>
              </div>
              <ImageIcon className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">待标注</p>
                <p className="text-2xl font-bold">{pendingCount}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">已完成</p>
                <p className="text-2xl font-bold">{completedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">剩余配额</p>
                <p className="text-2xl font-bold">{user?.quota || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>操作</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload */}
          <div>
            <label className="block">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {uploading ? '上传中...' : '点击或拖拽上传图片'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  支持 JPG、PNG、WebP，单张最大 10MB
                </p>
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files)}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Start Annotation */}
          {pendingCount > 0 && (
            <Button
              onClick={handleStartAnnotation}
              disabled={annotating || (user?.quota || 0) < pendingCount}
              className="w-full"
              size="lg"
            >
              <Play className="h-5 w-5 mr-2" />
              {annotating ? '标注中...' : `开始标注 (${pendingCount} 张)`}
            </Button>
          )}

          {(user?.quota || 0) < pendingCount && pendingCount > 0 && (
            <p className="text-sm text-red-600 text-center">
              配额不足，需要 {pendingCount}，剩余 {user?.quota || 0}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Images List */}
      <Card>
        <CardHeader>
          <CardTitle>图片列表</CardTitle>
        </CardHeader>
        <CardContent>
          {images.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p>还没有上传图片</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {images.map((image) => (
                <div key={image.id} className="relative group">
                  <div
                    className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary"
                    onClick={() => viewImageDetail(image)}
                  >
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs truncate">{image.originalName}</p>
                    <div className="flex items-center justify-between">
                      {getStatusBadge(image.status)}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(image.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Detail Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedImage?.image.originalName}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              {selectedImage.annotation ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className="bg-green-100 text-green-700">
                      置信度: {(selectedImage.annotation.confidence * 100).toFixed(1)}%
                    </Badge>
                    {selectedImage.annotation.reviewed && (
                      <Badge className="bg-blue-100 text-blue-700">已复核</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(selectedImage.annotation.result).map(([key, value]: [string, any]) => (
                      <div key={key} className="space-y-1">
                        <label className="text-sm font-medium text-gray-700">
                          {getLabel(key)}
                        </label>
                        <div className="flex items-center space-x-2">
                          <Input
                            value={value.value || ''}
                            placeholder="未识别"
                            readOnly
                          />
                          <Badge
                            variant="outline"
                            className={
                              value.confidence >= 0.8
                                ? 'border-green-500 text-green-700'
                                : value.confidence >= 0.5
                                ? 'border-yellow-500 text-yellow-700'
                                : 'border-red-500 text-red-700'
                            }
                          >
                            {(value.confidence * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {selectedImage.image.status === 'pending' && '待标注'}
                  {selectedImage.image.status === 'processing' && '处理中...'}
                  {selectedImage.image.status === 'failed' && '标注失败'}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getLabel(key: string): string {
  const labels: Record<string, string> = {
    product_name: '品名',
    brand: '品牌',
    specification: '规格',
    price: '价格',
    shelf_life: '保质期',
    production_date: '生产日期',
    ingredients: '成分',
    origin: '产地',
  };
  return labels[key] || key;
}
